import * as Encoding from 'encoding-japanese';
import { MedicineDetailResult, DealInfo, WholesaleFragment } from 'features/graphql';
import { resolvePriceTypeLabel } from './priceTypeLabel';

/**
 * 価格表画面（PriceShowList）の表示内容をそのままCSV化するためのユーティリティ。
 *
 * 仕様（総合メディカル様向け SMart-Web CSV出力）:
 * - 出力対象は「当年度の薬価があるもの」のみ（呼び出し側でフィルタ済みを想定）
 * - 列構成は価格表画面の表示項目に準拠
 * - 最安値列: GS1コードの右に、最安（梱包価格が最小）の卸略称をカンマ区切りで出力
 * - 文字コード: Shift_JIS / 改行コード: CRLF
 *
 * 価格・割引率はCSVでの集計しやすさを優先し、画面の桁区切り（toLocaleString）や
 * 「%」記号は付けず、数値のみを出力する（含める列・値そのものは画面と一致）。
 */

/** CSVフィールドのエスケープ（カンマ・ダブルクォート・改行を含む場合は引用符で囲む）。 */
const escapeCsvField = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** 後発区分（画面のバッジ表示と同じ判定: 後発 / AG / BS）。 */
const formatGenericType = (medicine: MedicineDetailResult): string => {
  const labels: string[] = [];
  if (medicine.generic === 1) labels.push('後発');
  if (medicine.authorizedGeneric === 1) labels.push('AG');
  if (medicine.biosimilar === 1) labels.push('BS');
  return labels.join('/');
};

/**
 * 卸ごとの価格種別ラベル（画面の表示と同一ロジック。判定は resolvePriceTypeLabel に集約）。
 * 取引が無い場合は空文字。
 */
const formatPriceTypeLabel = (deal: DealInfo | undefined, facilityStatus: string): string => {
  if (!deal) return '';
  return resolvePriceTypeLabel(facilityStatus, deal.priceType);
};

/**
 * 薬価の適用期間（開始日 - 終了日）。両端が無い場合は空文字。
 * セパレータは Shift_JIS で安全に変換できるハイフンを使用（波ダッシュは文字化けするため）。
 */
const formatPeriod = (start: string | null | undefined, end: string | null | undefined): string => {
  if (!start && !end) return '';
  if (start && end) return `${start} - ${end}`;
  return start ?? end ?? '';
};

/** 価格（画面 case 3 と同じく小数切り捨て。桁区切りは付けない）。 */
const formatPrice = (price: number | null | undefined): string => {
  if (price === null || price === undefined) return '';
  return String(Math.floor(price));
};

/** 割引率（画面 case 4 と同じく小数2桁。「%」記号は付けない）。 */
const formatDiscountRate = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined) return '';
  return typeof rate === 'number' ? rate.toFixed(2) : String(rate);
};

/** ヘッダーに使う卸名（改行を空白へ正規化）。 */
const normalizeWholesaleName = (name: string | null | undefined): string =>
  (name ?? '').replace(/\s*\n\s*/g, ' ').trim();

/**
 * 最安値列: 梱包価格が最小の卸の略称をカンマ区切りで返す。
 * 判定は画面の「安」マークと同じ（最小価格に一致する卸すべて。価格が1卸のみでもその卸を出力）。
 * 価格のある取引が無い場合は空文字。
 */
const formatCheapestWholesaleNames = (
  wholesales: WholesaleFragment[],
  dealByWholesaleId: Map<string, DealInfo>
): string => {
  let minPrice: number | null = null;
  wholesales.forEach((w) => {
    const price = w.wholesaleId ? dealByWholesaleId.get(w.wholesaleId)?.packagingPrice : null;
    if (price != null && (minPrice === null || price < minPrice)) {
      minPrice = price;
    }
  });
  if (minPrice === null) return '';

  return wholesales
    .filter(
      (w) => w.wholesaleId && dealByWholesaleId.get(w.wholesaleId)?.packagingPrice === minPrice
    )
    .map((w) => normalizeWholesaleName(w.pricelistWholesaleName ?? w.wholesaleName))
    .join(',');
};

/**
 * 価格表データからCSV文字列（ヘッダー込み・CRLF区切り）を生成する。
 *
 * @param medicines 出力対象の品目（当年度薬価ありで絞り込み済みを想定）
 * @param wholesales 表示対象の卸（画面ヘッダーと同じ順序）
 * @param facilityStatus 施設の暫定/妥結ステータス（価格種別ラベルの判定に使用）
 */
export const buildPriceShowCsv = (
  medicines: MedicineDetailResult[],
  wholesales: WholesaleFragment[],
  facilityStatus: string
): string => {
  // --- ヘッダー行 ---
  const fixedHeaders = [
    '区分',
    '後発区分',
    '品名',
    '規格',
    '一般名',
    '薬効分類',
    '販売元名',
    '当年度薬価',
    '当年度適用期間',
    '前年度薬価',
    '前年度適用期間',
    'JANコード',
    'YJコード',
    'GS1コード',
    '最安値',
  ];
  const wholesaleHeaders = wholesales.flatMap((w) => {
    // 略称（pricelistWholesaleName）を優先。画面ヘッダーと同じ表記。
    const name = normalizeWholesaleName(w.pricelistWholesaleName ?? w.wholesaleName);
    return [`${name} 価格種別`, `${name} 価格`, `${name} 割引率`, `${name} 購入実績`];
  });
  const headers = [...fixedHeaders, ...wholesaleHeaders];

  // --- データ行 ---
  const rows = medicines.map((medicine) => {
    const dealByWholesaleId = new Map<string, DealInfo>();
    (medicine.deals ?? []).forEach((deal) => {
      if (deal?.wholesaleId) dealByWholesaleId.set(deal.wholesaleId, deal);
    });

    const fixedCols = [
      medicine.medicineType,
      formatGenericType(medicine),
      medicine.medicineName,
      medicine.capacity,
      medicine.commonName,
      medicine.drugCategoryName,
      medicine.salesManufacturer,
      formatPrice(medicine.currentPrice),
      formatPeriod(medicine.currentStartDate, medicine.currentEndDate),
      formatPrice(medicine.previousPrice),
      formatPeriod(medicine.previousStartDate, medicine.previousEndDate),
      medicine.janCode,
      medicine.yjCode,
      medicine.gs1Code,
      formatCheapestWholesaleNames(wholesales, dealByWholesaleId),
    ];

    const wholesaleCols = wholesales.flatMap((w) => {
      const deal = w.wholesaleId ? dealByWholesaleId.get(w.wholesaleId) : undefined;
      return [
        formatPriceTypeLabel(deal, facilityStatus),
        formatPrice(deal?.packagingPrice),
        formatDiscountRate(deal?.discountRate),
        deal?.hasPurchaseHistoryForYear ? '○' : '',
      ];
    });

    return [...fixedCols, ...wholesaleCols].map(escapeCsvField).join(',');
  });

  return [headers.map(escapeCsvField).join(','), ...rows].join('\r\n');
};

/** Shift_JISエンコードしたCSVをブラウザでダウンロードさせる。 */
export const downloadCsv = (filename: string, csvText: string): void => {
  const unicodeCodes = Encoding.stringToCode(csvText);
  const sjisCodes = Encoding.convert(unicodeCodes, { to: 'SJIS', from: 'UNICODE' });
  const blob = new Blob([new Uint8Array(sjisCodes)], { type: 'text/csv' });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
